import classnames from 'classnames';
import isInternalLink from 'is-internal-link';
import React from 'react';

import { DEFAULT_SCROLL_CONFIG } from '@constants/scroll';

import { GatsbyLink, NormalLink, ReactScrollLink } from './styles';

export type PropTypes = {
  href?: string;
  children?: React.ReactNode;
  className?: string;
  dataTestId: string;
  onClick?: (event: React.MouseEvent | undefined) => void;
  activeClassName?: string;
  partiallyActive?: boolean;
  noStyles?: boolean;
  forceActive?: boolean;
  target?: string;
};

const defaultHandler = (e: React.MouseEvent): void => {
  e.persist();
  e.preventDefault();
};

const Link = ({
  href,
  children,
  dataTestId,
  onClick,
  className,
  activeClassName,
  partiallyActive = false,
  noStyles,
  forceActive,
  target,
  ...props
}: PropTypes): JSX.Element => {
  const baseClass = forceActive ? classnames(activeClassName, className) : className;
  const activeClass = noStyles ? undefined : activeClassName;
  const normalClass = noStyles ? classnames('no-styles', baseClass) : baseClass;

  if (!href) {
    const handler = onClick || defaultHandler;

    return (
      <NormalLink
        data-test-id={dataTestId}
        className={classnames(normalClass, !noStyles ? 'no-href' : undefined)}
        onClick={handler}
        target={target}
        {...props}
      >
        {children}
      </NormalLink>
    );
  }

  const isElementLink = href?.startsWith('#');

  if (href && isElementLink) {
    return (
      <ReactScrollLink
        activeClass={activeClass}
        className={normalClass}
        to={href?.slice(1)}
        smooth={DEFAULT_SCROLL_CONFIG.SMOOTH}
        offset={DEFAULT_SCROLL_CONFIG.OFFSET}
        duration={DEFAULT_SCROLL_CONFIG.DURATION}
        onClick={(): void => {
          window.history.pushState('', '', href);

          if (onClick) onClick(undefined);
        }}
        spy
        href={href}
      >
        {children}
      </ReactScrollLink>
    );
  }

  if (!isInternalLink(href)) {
    return (
      <NormalLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-test-id={dataTestId}
        className={normalClass}
        onClick={onClick}
        {...props}
      >
        {children}
      </NormalLink>
    );
  }

  return (
    <GatsbyLink
      target={target}
      activeClassName={activeClass}
      className={normalClass}
      onClick={onClick}
      to={href}
      data-test-id={dataTestId}
      partiallyActive={partiallyActive}
    >
      {children}
    </GatsbyLink>
  );
};

export default Link;
